/**
 * Shared URL parsing and building for table state (pagination + sort).
 * Used by Fleet Overview and Operating Points so page, perPage, sort column,
 * and sort direction persist in the URL and when navigating back.
 */

const DEFAULT_PAGE = 1;
const DEFAULT_PER_PAGE = 10;

/**
 * Parse table state from a URL search string.
 * @param {string} searchString - Search string (with or without leading "?")
 * @param {Set<string>} [validSortColumns] - If provided, only accept sort column when in this set
 * @returns {{ page: number, itemsPerPage: number, sortColumn: string|null, sortDirection: 'asc'|'desc' }}
 */
export function parseTableStateFromSearchString(
  searchString,
  validSortColumns
) {
  const params = new URLSearchParams(
    searchString?.startsWith("?") ? searchString : `?${searchString || ""}`
  );
  const page = params.get("page");
  const perPage = params.get("perPage");
  const sort = params.get("sort");
  const dir = params.get("dir");
  const pageNum = page != null ? parseInt(page, 10) : null;
  const perPageNum = perPage != null ? parseInt(perPage, 10) : null;
  const sortColumn =
    sort != null && sort !== ""
      ? validSortColumns == null || validSortColumns.has(sort)
        ? sort
        : null
      : null;
  const sortDirection = dir === "asc" || dir === "desc" ? dir : "asc";
  return {
    page: typeof pageNum === "number" && pageNum >= 1 ? pageNum : DEFAULT_PAGE,
    itemsPerPage:
      typeof perPageNum === "number" && perPageNum >= 1
        ? perPageNum
        : DEFAULT_PER_PAGE,
    sortColumn,
    sortDirection,
  };
}

/**
 * Build URLSearchParams for table state (only adds params when non-default).
 * @param {Object} state
 * @param {number} state.page - Current page
 * @param {number} state.itemsPerPage - Rows per page
 * @param {string|null} state.sortColumn - Sorted column key
 * @param {'asc'|'desc'} state.sortDirection - Sort direction
 * @param {Object} [options]
 * @param {number} [options.defaultPerPage=10] - Default per page; only add perPage when !== this
 * @returns {URLSearchParams}
 */
export function buildTableStateSearchParams(state, options = {}) {
  const { defaultPerPage = DEFAULT_PER_PAGE } = options;
  const params = new URLSearchParams();
  if (state.page > DEFAULT_PAGE) params.set("page", String(state.page));
  if (state.itemsPerPage !== defaultPerPage)
    params.set("perPage", String(state.itemsPerPage));
  if (state.sortColumn) {
    params.set("sort", state.sortColumn);
    params.set("dir", state.sortDirection || "asc");
  }
  return params;
}

/**
 * Append table state to an existing URL search string (e.g. for asset links).
 * Preserves existing params and adds page, perPage, sort, dir when non-default.
 * @param {URLSearchParams|string} existing - Existing query or search string
 * @param {Object} state - { page, itemsPerPage, sortColumn, sortDirection }
 * @param {Object} [options] - { defaultPerPage }
 * @returns {string} Query string (no leading "?")
 */
export function appendTableStateToSearch(existing, state, options = {}) {
  const params =
    existing instanceof URLSearchParams
      ? new URLSearchParams(existing)
      : new URLSearchParams(
          typeof existing === "string" && existing.startsWith("?")
            ? existing.slice(1)
            : existing || ""
        );
  if (state.page > DEFAULT_PAGE) params.set("page", String(state.page));
  const { defaultPerPage = DEFAULT_PER_PAGE } = options;
  if (state.itemsPerPage !== defaultPerPage)
    params.set("perPage", String(state.itemsPerPage));
  if (state.sortColumn) {
    params.set("sort", state.sortColumn);
    params.set("dir", state.sortDirection || "asc");
  }
  return params.toString();
}

export { DEFAULT_PAGE, DEFAULT_PER_PAGE };

/**
 * Build the query string portion of an asset link from a source page that has
 * a paginated/sortable table. Encodes which page the user came from along
 * with the current table state (page, perPage, sort, dir) and any extra
 * source-specific params (e.g. `tab`, `optimizationEnabled`).
 *
 * @param {Object} params
 * @param {string} params.sourceParam - Identifier for the source page (e.g. "overview", "no-data-report")
 * @param {number} params.page - Current page
 * @param {number} params.itemsPerPage - Rows per page
 * @param {string|null} params.sortColumn - Sorted column key (or null)
 * @param {'asc'|'desc'} params.sortDirection - Sort direction
 * @param {Record<string, string|null|undefined>} [params.extras] - Extra params to include (skipped when null/undefined/"")
 * @param {Object} [options]
 * @param {number} [options.defaultPerPage=10]
 * @returns {string} Query string starting with "?"
 */
export function buildAssetLinkSearch(
  { sourceParam, page, itemsPerPage, sortColumn, sortDirection, extras = {} },
  options = {}
) {
  const params = new URLSearchParams();
  if (sourceParam) params.set("from", sourceParam);
  for (const [k, v] of Object.entries(extras)) {
    if (v != null && v !== "") params.set(k, String(v));
  }
  const tableParams = buildTableStateSearchParams(
    { page, itemsPerPage, sortColumn, sortDirection },
    options
  );
  for (const [k, v] of tableParams) params.set(k, v);
  const query = params.toString();
  return query ? `?${query}` : "";
}

/**
 * Build the back-navigation URL path for a source page based on URL params
 * present on the asset page (which carry forward the table state and any
 * extras the source originally provided).
 *
 * @param {Object} params
 * @param {string} params.basePath - Base path for the source (e.g. "/no-data-report")
 * @param {URLSearchParams} params.urlParams - URLSearchParams from the current asset page URL
 * @param {string[]} [params.preserveExtras] - Extra param keys to forward through (e.g. ["optimizationEnabled", "tab"])
 * @param {Set<string>} [params.validSortColumns] - If provided, only forward sort when in this set
 * @returns {string} Full path with query string, suitable for navigation
 */
export function buildBackToSourcePath({
  basePath,
  urlParams,
  preserveExtras = [],
  validSortColumns,
}) {
  const parsed = parseTableStateFromSearchString(
    urlParams.toString(),
    validSortColumns
  );
  const out = new URLSearchParams();
  for (const key of preserveExtras) {
    const v = urlParams.get(key);
    if (v != null && v !== "") out.set(key, v);
  }
  const tableParams = buildTableStateSearchParams(parsed);
  for (const [k, v] of tableParams) out.set(k, v);
  const query = out.toString();
  return query ? `${basePath}?${query}` : basePath;
}
